import { overlayTenantColours, type TenantBranding } from "@tenant-theme";

interface ThemeColors {
  dark: boolean;
  colors: Record<string, string>;
}

interface PaletteColors {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
  foreground: string;
  navbar: string;
  /**
   * `surface` is the token EVERY Vuetify component paints itself with —
   * v-card, v-list, v-menu, v-app-bar, v-navigation-drawer. It was missing
   * from this palette, so all of them fell back to Vuetify's stock surface
   * (#FFFFFF light / #212121 dark) while the shell's own hand-written CSS used
   * `foreground` (#1F2937). That single gap is why the org picker's list and
   * every menu read as a different product to legacy-client. `card`/`raised`
   * are the house aliases the Vue3 modules already use (modules/crm, portal).
   */
  surface: string;
  card: string;
  raised: string;
  /** Explicit, so Vuetify does not derive a contrast colour of its own. */
  "on-surface": string;
  "on-background": string;
  primaryText: string;
  lightText: string;
  darkText: string;
  /** Status colours — mirrored from utils/theme.js, not Vuetify's Material defaults. */
  error: string;
  success: string;
  warning: string;
  info: string;
  alert: string;
  edit: string;
}

// Palette source of truth: metaship-frontend/utils/theme.js (light :1-52, dark
// :53-90) — mirrored, not imported cross-package (same convention as
// modules/portal/src/plugins/vuetify.ts and modules/crm `sharedColors`). Do not
// diverge without updating BOTH that file and this one.
const SHARED_SOURCE = {
  primary: "#649ECE",
  secondary: "#316B9B",
  tertiary: "#007d5c",
  error: "#DC4C41",
  success: "#007d5c",
  warning: "#E7A136",
  info: "#649ECE",
  alert: "#DC4C41",
  edit: "#4189C3",
  lightText: "#FFFFFF",
  darkText: "#050505",
};

const LIGHT_SOURCE: PaletteColors = {
  ...SHARED_SOURCE,
  background: "#E7EDF3",
  foreground: "#FFFFFF",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  raised: "#FFFFFF",
  navbar: "#27567C",
  "on-surface": "#2C3644",
  "on-background": "#2C3644",
  primaryText: "#050505",
};

const DARK_SOURCE: PaletteColors = {
  ...SHARED_SOURCE,
  background: "#111927",
  foreground: "#1F2937",
  surface: "#1F2937",
  card: "#1F2937",
  raised: "#2D3C50",
  navbar: "#1F2937",
  "on-surface": "#E6EAF2",
  "on-background": "#E6EAF2",
  primaryText: "#f2f2f2",
};

/** Vuetify-4 shaped `{ light, dark }` themes, tenant colours overlaid (Part D contract). */
export function buildTenantThemes(branding: TenantBranding | null): { light: ThemeColors; dark: ThemeColors } {
  const light: ThemeColors = { dark: false, colors: { ...LIGHT_SOURCE, accent: LIGHT_SOURCE.tertiary } };
  const dark: ThemeColors = { dark: true, colors: { ...DARK_SOURCE, accent: DARK_SOURCE.tertiary } };
  if (!branding) return { light, dark };
  return {
    light: { dark: false, colors: overlayTenantColours(light.colors, false, branding) },
    dark: { dark: true, colors: overlayTenantColours(dark.colors, true, branding) },
  };
}

function upsertMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertFavicon(href: string): void {
  let el = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "icon";
    document.head.appendChild(el);
  }
  el.href = href;
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** Only a strict `#rrggbb` value is trusted from tenant-supplied branding — anything else falls back to the Metaship default. */
function safeColour(value: string | null | undefined, fallback: string): string {
  return value && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * Does this app stand under its OWN name and mark (DEV-925)? A WHITE_LABEL app
 * always does — it is the customer's brand and must never show ours. A PRODUCT
 * app is "Metaship | <app>" unless its branding says `identity: "own"` (Boer:
 * its own glyph, its own favicon, no cube). Every place the shell decides
 * whether to draw the Metaship name or mark reads THIS, never `kind` directly.
 */
export function hasOwnIdentity(
  kind: "WHITE_LABEL" | "PRODUCT" | null | undefined,
  branding: Pick<TenantBranding, "identity"> | null | undefined,
): boolean {
  return kind === "WHITE_LABEL" || branding?.identity === "own";
}

/**
 * Brand the DOM: title, favicon, theme-color, and `:root` CSS vars every
 * module's `@tenant-theme` overlay + the shell's own chrome read from.
 * Falls back to the Metaship defaults when a colour is unset OR malformed
 * (branding contract: all keys nullable — Appendix, `branding` JSON; a bad
 * value here would otherwise land in a `style.setProperty()` call and/or a
 * Vuetify theme colour completely unvalidated).
 */
export function applyBranding(
  name: string,
  branding: TenantBranding | null,
  root: HTMLElement = document.documentElement,
): void {
  document.title = name;

  const defaultThemeIsDark = branding?.defaultTheme !== "light";
  const source = defaultThemeIsDark ? DARK_SOURCE : LIGHT_SOURCE;
  const colours = branding?.colours;

  const primary = safeColour(colours?.primary, source.primary);
  const secondary = safeColour(colours?.secondary, source.secondary);
  const tertiary = safeColour(colours?.tertiary, source.tertiary);
  const background = safeColour(colours?.background, source.background);
  const surface = safeColour(colours?.surface, source.foreground);
  const navbar = safeColour(colours?.navbar, source.navbar);

  root.style.setProperty("--tenant-primary", primary);
  root.style.setProperty("--tenant-secondary", secondary);
  root.style.setProperty("--tenant-tertiary", tertiary);
  root.style.setProperty("--tenant-background", background);
  root.style.setProperty("--tenant-surface", surface);
  root.style.setProperty("--tenant-navbar", navbar);

  upsertMeta("theme-color", navbar);
  if (branding?.iconUrl) upsertFavicon(branding.iconUrl);
}
